import { Component, OnInit } from '@angular/core';
import { MdbModalRef } from 'mdb-angular-ui-kit/modal';
import {
  LocationPair,
  SIMULATION_SCENARIOS,
  SimulationActivityType,
  SimulationScenario,
} from 'src/app/utils';

export interface CustomSimulationModalResult {
  scenarioId?: string;
  survivor: LocationPair[];
  perpetrator: LocationPair[];
  scenarioTitle: string;
  activity?: SimulationActivityType;
}

@Component({
  selector: 'app-custom-simulation-modal',
  templateUrl: './custom-simulation-modal.component.html',
  styleUrls: ['./custom-simulation-modal.component.scss'],
  standalone: false,
})
export class CustomSimulationModalComponent implements OnInit {
  title = 'Run Simulation';
  selectedScenarioId = 'custom-draggable';
  showCustomCoordinates = false;
  survivorCoordinates = '';
  perpetratorCoordinates = '';
  coordinateError = '';
  readonly scenarios = SIMULATION_SCENARIOS;

  constructor(public modalRef: MdbModalRef<CustomSimulationModalComponent>) {}

  ngOnInit(): void {
    this.selectedScenarioId = this.scenarios[0].id;
  }

  get selectedScenario(): SimulationScenario {
    return this.scenarios.find((scenario) => scenario.id === this.selectedScenarioId) ?? this.scenarios[0];
  }

  run(): void {
    const scenario = this.selectedScenario;
    this.coordinateError = '';
    const survivor = this.parseCoordinates(this.survivorCoordinates, 'Survivor');
    const perpetrator = survivor
      ? this.parseCoordinates(this.perpetratorCoordinates, 'Perpetrator')
      : null;

    if (!survivor || !perpetrator) {
      return;
    }

    this.modalRef.close({
      scenarioId: scenario.id,
      survivor: survivor ?? scenario.survivorLocations,
      perpetrator: perpetrator ?? scenario.perpetratorLocations,
      scenarioTitle: scenario.title,
      activity: scenario.activity,
    } as CustomSimulationModalResult);
  }

  private parseCoordinates(value: string, party: string): LocationPair[] | null {
    if (!value.trim()) {
      return party === 'Survivor'
        ? this.selectedScenario.survivorLocations
        : this.selectedScenario.perpetratorLocations;
    }

    const trimmedValue = value.trim();
    try {
      const parsed = JSON.parse(trimmedValue) as unknown;
      if (Array.isArray(parsed) && parsed.length > 0 && parsed.every((pair) => this.isLocationPair(pair))) {
        return parsed as LocationPair[];
      }
    } catch {
      // Fall back to the one-pair-per-line format below.
    }

    const lines = value.split(/\r?\n|;/).map((line) => line.trim()).filter(Boolean);
    const coordinates: LocationPair[] = [];

    for (const line of lines) {
      const values = line.replace(/[\[\]()]/g, '').split(/[\s,]+/).filter(Boolean).map(Number);
      if (!this.isLocationPair(values)) {
        this.coordinateError = `${party} coordinates must use one longitude, latitude pair per line.`;
        return null;
      }
      coordinates.push(values as LocationPair);
    }

    return coordinates;
  }

  private isLocationPair(value: unknown): value is LocationPair {
    return Array.isArray(value)
      && value.length >= 2
      && value.length <= 3
      && value.every((coordinate) => typeof coordinate === 'number' && Number.isFinite(coordinate));
  }

  close(): void {
    this.modalRef.close();
  }
}
